/* eslint jsx-a11y/anchor-is-valid: 0 */
import classnamesBind from 'classnames/bind';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from '@concur/nui-widgets/lib/Link/Link';
import { withThemeStyles } from '@concur/ui-theming';
import compose from '../utils/compose';
import ErrorIllustration from './_ErrorIllustration';
import withErrorBoundary from '../utils/withErrorBoundary';
import withNuiFormatter from '../utils/withNuiFormatter';
import * as styles from './AppError-*.css';

export const ERRORS = {
    aspError: 'AspError',
    browser: 'Browser',
    clientError: 'ClientError',
    comingSoon: 'ComingSoon',
    downloadError: 'DownloadError',
    forbidden: 'Forbidden',
    notFound: 'NotFound',
    profileMoveError: 'ProfileMoveError',
    securityBreach: 'SecurityBreach',
    serverError: 'ServerError',
    timeout: 'Timeout',
};

const CSS_BLOCK = 'sapcnqr-app-error';

const Error = ({
    backUrl,
    className,
    classNameMap,
    correlationId,
    customDescription,
    customHeader,
    formatter,
    homeUrl,
    links,
    paragraphs,
    type,
}) => {
    const classnames = classnamesBind.bind(classNameMap);

    const errorType = ERRORS[type] || 'ServerError';

    const headerTranslationId = `CoreUI.errorPage_${errorType}.header`;
    const descriptionTranslationId = `CoreUI.errorPage_${errorType}.description`;

    const classes = classnames(CSS_BLOCK, className);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = customHeader || formatter.formattedMessage({ id: headerTranslationId });
        return () => {
            document.title = previousTitle;
        };
    }, [customHeader, headerTranslationId]);

    const hasCustomContent = customDescription || paragraphs.length > 0;

    return (
        <section className={classes}>
            <div className={classnames(`${CSS_BLOCK}__container`)}>
                <h1 className={classnames(`${CSS_BLOCK}__header`)}>
                    {customHeader || formatter.formattedMessage({ id: headerTranslationId })}
                </h1>
                {customDescription && (
                    <div className={classnames(`${CSS_BLOCK}__description`)}>
                        {customDescription}
                    </div>
                )}

                {paragraphs.map((paragraph) => (
                    <p key={paragraph.text} className={classnames(`${CSS_BLOCK}__description`, paragraph.className)}>
                        {paragraph.text}
                    </p>
                ))}

                {!hasCustomContent && (
                    <div className={classnames(`${CSS_BLOCK}__description`)}>
                        {formatter.formattedMessage({ id: descriptionTranslationId })}
                    </div>
                )}

                {correlationId && (
                    <div className={classnames(`${CSS_BLOCK}__error-description`)}>
                        {formatter.formattedMessage({
                            id: 'CoreUI.error.correlationid',
                            values: { correlationId },
                        })}
                    </div>
                )}

                {(backUrl || homeUrl || links.length > 0) && (
                    <>
                        <h2 className={classnames(`${CSS_BLOCK}__helpful-links-header`)}>
                            {formatter.formattedMessage({ id: 'CoreUI.errorPage_Error.helpful_links' })}
                        </h2>
                        <ul className={classnames(`${CSS_BLOCK}__helpful-links`)}>
                            {backUrl && (
                                <li className={classnames(`${CSS_BLOCK}__helpful-link-item`)}>
                                    <Link href={backUrl}>
                                        {formatter.formattedMessage({ id: 'CoreUI.errorPage_Error.back' })}
                                    </Link>
                                </li>
                            )}
                            {homeUrl && (
                                <li className={classnames(`${CSS_BLOCK}__helpful-link-item`)}>
                                    <Link href={homeUrl}>
                                        {formatter.formattedMessage({ id: 'CoreUI.errorPage_Error.home' })}
                                    </Link>
                                </li>
                            )}
                            {links.map((link) => (
                                <li className={classnames(`${CSS_BLOCK}__helpful-link-item`)} key={link.url}>
                                    <Link href={link.url}>
                                        {link.text}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
            <ErrorIllustration />
        </section>
    );
};

Error.propTypes = {
    backUrl: PropTypes.string,
    className: PropTypes.string,
    correlationId: PropTypes.string,
    customDescription: PropTypes.string,
    paragraphs: PropTypes.arrayOf(
        PropTypes.shape({
            className: PropTypes.string,
            text: PropTypes.string.isRequired,
        }),
    ),
    customHeader: PropTypes.string,
    homeUrl: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }),
    ),
    type: PropTypes.oneOf(Object.keys(ERRORS)),
};

Error.defaultProps = {
    links: [],
    paragraphs: [],
    type: 'serverError',
};

export default withThemeStyles(compose(
    withNuiFormatter,
    withErrorBoundary,
)(Error), styles);
